// "Continue with Google / Discord / Steam" on the organisation console's sign-in, sign-up and invitation page
// (F-IAM-03 … F-IAM-05, spec/07 §3.1, ADR-0046, ADR-0050), on the server's browser flow: the api answers the
// provider's URL, the provider sends the browser back through this console's own `/v1`, and the server lands it on
// `callbackURL` signed in, on `newUserCallbackURL` ("Complete your profile") for a new account, or on
// `errorCallbackURL` with `?error=<code>`.
//
//   Offered only where it works: `GET /v1/meta`'s `signIn.social` names the providers this deployment has switched
//   on. The HQ console offers none (staff-only, invitation-first).
//
//   No terms first (owner, 2026-10-08): a press goes straight to the provider. An existing account signs in. A
//   provider account with no Clavian account comes back `CONSENT_REQUIRED`, and "Create your Clavian account"
//   (`CreateAccountDialog`) asks for the terms and privacy, then starts the same round trip again with them: every
//   account is still created with its consent, and nothing about a person who has not agreed is held anywhere.
//
//   Nothing is loosened: the server refuses a competing vendor's domain as it does any sign-up
//   (`SIGN_UP_REFUSED`), keeps a provider's address only when the provider has verified it — and a verified address
//   counts as verified for an invitation, which still has to be for exactly that address — and never joins a
//   provider to an existing account by email (`account_not_linked`).
//
// The token of an invitation waiting to be accepted is in this browser's storage, not in these URLs: after the
// round trip the sign-in screens take the person back to it (`invitation-token.ts`).

import type { ApiClient, SignUpConsent, SocialProvider } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, Dialog, SocialButton, SocialIconButton } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApi } from '../api/client';
import { fetchBrandLegal } from '../api/meta';
import { queryKeys } from '../api/queries';
import { ConsentLabel } from './consent';
import { useSignInMethods } from './sign-in-methods';

/** The social providers this deployment offers: none until `GET /v1/meta` says otherwise. */
export function useSocialProviders(): readonly SocialProvider[] {
  return useSignInMethods().social;
}

/** `?error=` codes a provider round trip comes back with, as the screens' copy keys (`{{provider}}` filled in). */
export function socialErrorKey(code: string | undefined): string | undefined {
  switch (code) {
    case undefined:
    case '':
      return undefined;
    case 'CONSENT_REQUIRED':
      return 'social.error.noAccount';
    case 'account_not_linked':
    case 'OAUTH_LINK_ERROR':
      return 'social.error.notLinked';
    case 'SIGN_UP_REFUSED':
      return 'signUp.failure.refused';
    case 'BANNED_USER':
      return 'signIn.failure.banned';
    case 'access_denied':
      return 'social.error.cancelled';
    case 'state_mismatch':
    case 'state_not_found':
    case 'please_restart_the_process':
      return 'social.error.expired';
    default:
      return 'social.error.generic';
  }
}

/** Leaving for the provider: the browser's own navigation, replaceable in a test (jsdom navigates nowhere). */
export const providerNavigation = { assign: (url: string): void => window.location.assign(url) };

/** A path of this console as an absolute URL of it: what the server's trusted origins accept. */
export const here = (path: string): string => new URL(path, window.location.origin).toString();

/** `path` with `params` added to its query, keeping what it had. */
export function withQuery(path: string, params: Readonly<Record<string, string | undefined>>): string {
  const url = new URL(path, 'http://console.invalid');
  for (const [key, value] of Object.entries(params)) if (value !== undefined) url.searchParams.set(key, value);
  return `${url.pathname}${url.search}`;
}

export const isSocialProvider = (value: unknown): value is SocialProvider =>
  value === 'google' || value === 'discord' || value === 'steam';

export interface SocialTrip {
  /** Where the browser lands signed in: a path of this console. */
  readonly landing: string;
  /** Where it lands refused, with `?error=` and our own `provider=` added: a path of this console. */
  readonly onError: string;
  /** The consent a new account is created with: only on the second trip, from "Create your Clavian account". */
  readonly consent?: SignUpConsent;
}

/** The provider's URL for a sign-in (or sign-up) round trip; a new account lands on "Complete your profile". */
async function providerUrl(api: ApiClient, provider: SocialProvider, trip: SocialTrip): Promise<string> {
  const callbackURL = here(trip.landing);
  const errorCallbackURL = here(withQuery(trip.onError, { provider }));
  const newUserCallbackURL = here(withQuery('/welcome', { redirect: trip.landing }));
  const consent = trip.consent === undefined ? {} : { consent: trip.consent };
  if (provider === 'steam') {
    return (await api.steamStart({ body: { callbackURL, errorCallbackURL, newUserCallbackURL, ...consent } })).url;
  }
  const answer = await api.socialSignIn({ body: { provider, callbackURL, errorCallbackURL, newUserCallbackURL, disableRedirect: true, ...consent } });
  if (answer.url === undefined) throw new Error('No provider URL came back.');
  return answer.url;
}

function useStartSocial() {
  const api = useApi();
  const [starting, setStarting] = useState<SocialProvider | null>(null);
  const [failed, setFailed] = useState<SocialProvider | null>(null);
  const start = async (provider: SocialProvider, trip: SocialTrip) => {
    setFailed(null);
    setStarting(provider);
    try {
      providerNavigation.assign(await providerUrl(api, provider, trip));
    } catch {
      setFailed(provider);
      setStarting(null);
    }
  };
  return { start, starting, failed };
}

export type ContinueWithProvidersProps = Omit<SocialTrip, 'consent'>;

/**
 * "Continue with Google / Discord / Steam" on the organisation console's sign-in, sign-up and invitation page, each
 * only where `GET /v1/meta` has it on; nothing when none is. One provider is the full-width button; two or three
 * stand in a row as their marks alone, each named and with a tooltip, as on the player web and the kiosk. A press
 * goes straight to the provider, with no terms first (owner, 2026-10-08).
 */
export function ContinueWithProviders({ landing, onError }: ContinueWithProvidersProps): ReactNode {
  const { t } = useTranslation('console');
  const providers = useSocialProviders();
  const { start, starting, failed } = useStartSocial();
  if (providers.length === 0) return null;
  const press = (provider: SocialProvider) => void start(provider, { landing, onError });

  return (
    <div className="console-social">
      <p className="console-social__or" aria-hidden="true">
        <span>{t('social.or')}</span>
      </p>
      {providers.length === 1 ? (
        providers.map((provider) => (
          <SocialButton key={provider} provider={provider} busy={starting === provider} onClick={() => press(provider)}>
            {t(`social.continue.${provider}`)}
          </SocialButton>
        ))
      ) : (
        <div className="clav-social-row" role="group" aria-label={t('social.groupLabel')}>
          {providers.map((provider) => (
            <SocialIconButton key={provider} provider={provider} label={t(`social.continue.${provider}`)} busy={starting === provider} onClick={() => press(provider)} />
          ))}
        </div>
      )}
      {failed === null ? null : (
        <p className="console-alert" role="alert">
          {t('social.error.startProvider', { provider: t(`social.provider.${failed}`) })}
        </p>
      )}
    </div>
  );
}

export interface CreateAccountDialogProps extends Omit<SocialTrip, 'consent'> {
  /** The provider whose round trip came back `CONSENT_REQUIRED`; `null` while the dialog is shut. */
  readonly provider: SocialProvider | null;
  readonly onClose: () => void;
}

/**
 * "Create your Clavian account": shown only when a provider account has no Clavian account yet (`CONSENT_REQUIRED`).
 * The terms and privacy in force are agreed here, in a centred dialog, and the same provider's round trip starts
 * again with them; the account is created on its way back (spec/07 §3.1). An existing account never sees it.
 */
export function CreateAccountDialog({ provider, landing, onError, onClose }: CreateAccountDialogProps): ReactNode {
  const { t } = useTranslation('console');
  const api = useApi();
  const legal = useQuery({ queryKey: queryKeys.meta, queryFn: () => fetchBrandLegal(api), staleTime: 300_000 });
  const { start, starting, failed } = useStartSocial();
  const [agreed, setAgreed] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [needsConsent, setNeedsConsent] = useState(false);
  const name = provider === null ? '' : t(`social.provider.${provider}`);

  const go = async () => {
    if (provider === null || legal.data === undefined) return;
    if (!agreed) {
      setNeedsConsent(true);
      return;
    }
    const consent: SignUpConsent = { terms: legal.data.terms.version, privacy: legal.data.privacy.version, ...(marketing ? { marketing: true } : {}) };
    await start(provider, { landing, onError, consent });
  };

  return (
    <Dialog
      title={t('social.dialog.title')}
      description={t('social.dialog.body', { provider: name })}
      open={provider !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('social.dialog.cancel')}
          </Button>
          <Button variant="primary" busy={provider !== null && starting === provider} disabled={legal.data === undefined} onClick={() => void go()}>
            {t('social.dialog.confirm', { provider: name })}
          </Button>
        </>
      }
    >
      {legal.data === undefined ? (
        <p className="console-alert" role="status">
          {legal.isPending ? t('app.loading') : t('signUp.legal.unavailable')}
        </p>
      ) : (
        <div className="console-form">
          <Checkbox
            label={<ConsentLabel legal={legal.data} />}
            checked={agreed}
            onCheckedChange={(checked) => {
              setAgreed(checked === true);
              if (checked === true) setNeedsConsent(false);
            }}
            aria-invalid={needsConsent}
          />
          {needsConsent ? (
            <p className="console-alert" role="alert">
              {t('signUp.consent.required')}
            </p>
          ) : null}
          <Checkbox label={t('signUp.consent.marketing')} checked={marketing} onCheckedChange={(checked) => setMarketing(checked === true)} />
          {failed === null ? null : (
            <p className="console-alert" role="alert">
              {t('social.error.startProvider', { provider: name })}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
